import type { Baseline } from '@repo/contracts';

import { DEFAULT_TEMPLATE_STATUSES, SavedTemplate, TemplateStatus } from '@/types/saved-template';
import type {
  DemandScaling,
  RotaTemplateScope,
  SchedulingRuleset,
  ShiftSet,
  TemplateType,
} from '@/types/template';

import { backendRequest, backendRequestVoid } from './http-client';

/**
 * Template definition as stored in the database (JSONB). Contract-shaped: the
 * wizard persists contract types directly, so this aligns with `RotaTemplate`
 * (plus the wizard-only `ShiftSet` extras and a metadata block). The backend
 * treats it as opaque JSONB.
 */
export interface TemplateDefinition {
  name: string;
  type: TemplateType;
  description: string;
  scope: RotaTemplateScope;
  shifts: ShiftSet[];
  schedulingRules: SchedulingRuleset;
  /** UI scaling settings (editing truth, round-tripped for reload). */
  demandScaling?: DemandScaling;
  /** Contract baseline derived from `demandScaling` for rotagen (Phase 4). */
  baseline?: Baseline;
  metadata?: {
    createdBy?: string;
    modifiedBy?: string;
    totalShifts?: number;
  };
}

export interface CreateTemplateResponse {
  id: string;
}

export type UpdateTemplateResponse = TemplateResponse;

export interface TemplateResponse {
  id: string;
  name: string;
  definition: TemplateDefinition;
  /**
   * Server-derived lifecycle status. Lifecycle changes only through the
   * restore, publish and unpublish action endpoints (and delete).
   */
  status: TemplateStatus;
  /** Server-derived publication timestamp; `null` until first published. */
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/**
 * Lists templates in the given lifecycle statuses (OR-ed, filtered server-side).
 * An empty or omitted list means the default: draft and published.
 *
 * @param statuses - Lifecycle statuses to include
 * @returns Array of templates
 * @throws Error if the API call fails
 */
export async function listTemplates(
  statuses: readonly TemplateStatus[] = DEFAULT_TEMPLATE_STATUSES,
): Promise<TemplateResponse[]> {
  const requested = statuses.length > 0 ? statuses : DEFAULT_TEMPLATE_STATUSES;
  return backendRequest<TemplateResponse[]>(
    `/v1/templates?status=${requested.join(',')}`,
    'Failed to list templates',
  );
}

/**
 * Gets a single template by ID from the backend API.
 *
 * @param id - The template ID to fetch
 * @returns The template data
 * @throws Error if the API call fails or template not found
 */
export async function getTemplate(id: string): Promise<TemplateResponse> {
  return backendRequest<TemplateResponse>(`/v1/templates/${id}`, 'Failed to get template');
}

/**
 * Creates a new template by sending it to the backend API.
 *
 * @param template - The template definition to create
 * @returns The created template ID
 * @throws Error if the API call fails
 */
export async function createTemplate(
  template: TemplateDefinition,
): Promise<CreateTemplateResponse> {
  return backendRequest<CreateTemplateResponse>('/v1/templates', 'Failed to create template', {
    method: 'POST',
    body: JSON.stringify(template),
  });
}

/**
 * Updates an existing template by ID.
 *
 * @param id - The template ID to update
 * @param template - The updated template definition
 * @returns The updated template
 * @throws Error if the API call fails or template not found
 */
export async function updateTemplate(
  id: string,
  template: TemplateDefinition,
): Promise<UpdateTemplateResponse> {
  return backendRequest<UpdateTemplateResponse>(
    `/v1/templates/${id}`,
    'Failed to update template',
    { method: 'PUT', body: JSON.stringify(template) },
  );
}

/**
 * Soft-deletes a template by ID.
 *
 * @param id - The template ID to delete
 * @throws Error if the API call fails or template not found
 */
export async function deleteTemplate(id: string): Promise<void> {
  await backendRequestVoid(`/v1/templates/${id}`, 'Failed to delete template', {
    method: 'DELETE',
  });
}

/**
 * Restores a soft-deleted template to the draft or published state it had
 * before deletion. Restoring a live template is a no-op on the server.
 */
export async function restoreTemplate(id: string): Promise<TemplateResponse> {
  return backendRequest<TemplateResponse>(
    `/v1/templates/${id}/restore`,
    'Failed to restore template',
    { method: 'POST' },
  );
}

/**
 * Publishes a draft template. Publishing an already-published template is a
 * no-op on the server; a deleted template is not found (restore it first).
 */
export async function publishTemplate(id: string): Promise<TemplateResponse> {
  return backendRequest<TemplateResponse>(
    `/v1/templates/${id}/publish`,
    'Failed to publish template',
    { method: 'POST' },
  );
}

/**
 * Returns a published template to draft. Unpublishing a draft is a no-op on
 * the server; a deleted template is not found (restore it first).
 */
export async function unpublishTemplate(id: string): Promise<TemplateResponse> {
  return backendRequest<TemplateResponse>(
    `/v1/templates/${id}/unpublish`,
    'Failed to unpublish template',
    { method: 'POST' },
  );
}

/**
 * Duplicates a template by creating a copy with a new name.
 * The server always creates the duplicate as a draft.
 *
 * @param id - The template ID to duplicate
 * @param newName - Optional new name for the duplicate (defaults to "Copy of [original name]")
 * @returns The created template ID
 * @throws Error if the API call fails
 */
export async function duplicateTemplate(
  id: string,
  newName?: string,
): Promise<CreateTemplateResponse> {
  const original = await getTemplate(id);
  const definition = original.definition;

  const duplicateData: TemplateDefinition = {
    ...definition,
    name: newName || `Copy of ${definition.name || 'Template'}`,
  };

  return createTemplate(duplicateData);
}

/**
 * Converts a TemplateResponse from the API to a SavedTemplate for the UI.
 * The persisted definition is already contract-shaped, so this is a thin map
 * that takes the server-derived status and fills metadata defaults.
 */
export function mapTemplateResponseToSavedTemplate(response: TemplateResponse): SavedTemplate {
  const definition = response.definition;

  return {
    id: response.id,
    name: definition.name || response.name,
    type: definition.type || 'standard',
    scope: definition.scope ?? {},
    description: definition.description ?? '',
    shifts: definition.shifts ?? [],
    schedulingRules: definition.schedulingRules ?? { rules: [] },
    demandScaling: definition.demandScaling,
    status: response.status,
    createdAt: response.createdAt,
    createdBy: definition.metadata?.createdBy || 'System',
    modifiedAt: response.updatedAt,
    modifiedBy: definition.metadata?.modifiedBy || 'System',
  };
}

/**
 * True when a persisted definition matches the current contract shape: a
 * `shifts` array whose every entry carries a `staffing` object and a `schedule`.
 *
 * Pre-production templates saved before the contracts migration have the old
 * flat shape (`staffingMethod`/`staffingConfig`, `daysOfWeek`); reading them as
 * the new shape would crash downstream (`shift.staffing.method`). They are meant
 * to be wiped — this predicate lets the list drop them defensively rather than
 * white-screen on a single stale row.
 */
export function isContractShapedDefinition(definition: TemplateDefinition | undefined): boolean {
  if (!definition || !Array.isArray(definition.shifts)) return false;
  return (definition.shifts as unknown[]).every((entry) => {
    const shift = entry as { staffing?: unknown; schedule?: unknown };
    return (
      shift != null &&
      typeof shift.staffing === 'object' &&
      shift.staffing !== null &&
      shift.schedule !== undefined
    );
  });
}

/**
 * Lists all templates from the backend API and converts them to SavedTemplate format.
 * Definitions that predate the contracts migration are dropped (see
 * {@link isContractShapedDefinition}) so a stale row can't crash the list.
 *
 * @param statuses - Lifecycle statuses to include (defaults to draft and published)
 * @returns Array of SavedTemplates
 * @throws Error if the API call fails
 */
export async function listTemplatesAsSaved(
  statuses?: readonly TemplateStatus[],
): Promise<SavedTemplate[]> {
  const responses = await listTemplates(statuses);
  const usable = responses.filter((response) => isContractShapedDefinition(response.definition));
  const dropped = responses.length - usable.length;
  if (dropped > 0) {
    console.warn(
      `Ignoring ${dropped} template(s) with a pre-contracts definition shape — re-save or wipe them.`,
    );
  }
  return usable.map(mapTemplateResponseToSavedTemplate);
}
